import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ChevronDown, LogOut, UserCircle2, Shield, User } from "lucide-react";

const ROLE_LABEL_MAP = {
  oneb_super_admin: "Super Admin",
  org_admin: "Organization Admin",
  business_owner: "Business Owner",
  general_manager: "General Manager",
  branch_manager: "Branch Manager",
  event_manager: "Event Manager",
  finance_manager: "Finance Manager",
  cashier: "Cashier",
  staff: "Staff",
};

function roleLabel(role) {
  if (!role) return "Member";
  return ROLE_LABEL_MAP[role]
    || role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Compact profile dropdown — replaces the old "user pill + separate logout"
 * pair.  Logout lives inside the dropdown.
 */
export default function UserMenu() {
  const { user, logout, activeMembership } = useAuth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const displayName = user?.name || user?.email || "";
  const initials = (displayName || "?").split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
  const role = user?.platform_role === "oneb_super_admin"
    ? "Super Admin"
    : roleLabel(activeMembership?.role);

  return (
    <div className="relative" ref={rootRef} data-testid="user-menu">
      <button
        type="button"
        data-testid="user-menu-btn"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label="Account menu"
        className="h-[38px] pl-1 pr-2 flex items-center gap-2 rounded-[10px] hover:bg-foreground/[0.06] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
      >
        <span className="h-8 w-8 shrink-0 grid place-items-center rounded-full bg-gradient-to-br from-primary to-secondary text-white text-[11px] font-semibold">
          {initials}
        </span>
        <span className="hidden md:flex flex-col leading-tight text-left min-w-0 max-w-[140px]">
          <span className="text-[13px] font-medium ink truncate" data-testid="user-menu-name">
            {displayName}
          </span>
          <span className="text-[10.5px] ink-muted truncate mt-[1px]" data-testid="user-menu-role">
            {role}
          </span>
        </span>
        <ChevronDown
          size={14}
          className={`hidden md:block ink-muted transition-transform duration-150 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-64 rounded-xl border hairline bg-popover text-popover-foreground shadow-2xl backdrop-blur-xl overflow-hidden z-50"
          role="menu"
          data-testid="user-menu-panel"
        >
          <div className="px-3 py-3 flex items-center gap-3 border-b hairline-soft">
            <span className="h-9 w-9 grid place-items-center rounded-full bg-gradient-to-br from-primary to-secondary text-white text-[12px] font-semibold">
              {initials}
            </span>
            <div className="min-w-0 leading-tight">
              <div className="text-[13px] font-semibold ink truncate">{displayName}</div>
              <div className="text-[11px] ink-muted truncate mt-0.5">{user?.email}</div>
            </div>
          </div>

          <div className="py-1.5">
            <Link
              to="/me"
              onClick={() => setOpen(false)}
              data-testid="user-menu-my-profile"
              role="menuitem"
              className="w-full px-3 py-2 flex items-center gap-2.5 text-[12.5px] ink hover:bg-foreground/[0.05] transition-colors"
            >
              <User size={14} className="ink-muted shrink-0" />
              <span>My Profile</span>
            </Link>
            <div className="px-3 py-2 flex items-center gap-2.5 text-[12.5px]">
              <UserCircle2 size={14} className="ink-muted shrink-0" />
              <span className="ink-muted">Account</span>
              <span className="ml-auto ink truncate max-w-[130px]" title={user?.email}>
                {user?.email}
              </span>
            </div>
            <div className="px-3 py-2 flex items-center gap-2.5 text-[12.5px]">
              <Shield size={14} className="ink-muted shrink-0" />
              <span className="ink-muted">Role</span>
              <span className="ml-auto ink">{role}</span>
            </div>
          </div>

          <div className="border-t hairline-soft py-1">
            <button
              type="button"
              onClick={() => { setOpen(false); logout(); }}
              data-testid="user-menu-signout"
              role="menuitem"
              className="w-full px-3 py-2.5 flex items-center gap-2.5 text-[13px] ink hover:bg-red-500/10 hover:text-red-400 transition-colors"
            >
              <LogOut size={14} />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
